// React
import { useEffect, useState } from 'react';
// React Navigator
import { useNavigate } from 'react-router-dom';
// Contexts
import { useCorreios } from '@contexts/';
// Services
import {
  createColetaToken,
  createToken,
  getCurrentTicketBrand,
  getInternalComments,
  getPostage,
  popupError,
  withTokenRetry,
  zafClient,
} from '@services/';
// Interfaces
import type { IGetPostage } from './interfaces';

import type { IZendeskIncidents } from '@interfaces/';

export interface IBrandOption {
  id: number;
  label: string;
  area: string;
}

// Nomes exatos das brands no Zendesk (normalizados: sem acento, maiúsculo, sem espaço extra)
const BRAND_FILTERS: { exactName: string; area: string; label: string }[] = [
  { exactName: 'JURIDICO', area: 'AC', label: 'AC' },
  { exactName: 'SAC AT', area: 'SAC AT', label: 'SAC AT' },
  { exactName: 'SAC ECOM', area: 'SAC E-COM', label: 'SAC E-COM' },
];

const normalizeName = (value?: string) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .trim();

const matchBrandFilter = (brandName: string) =>
  BRAND_FILTERS.find((f) => normalizeName(brandName) === f.exactName);

export function useHome() {
  const [loading, setLoading] = useState(true);
  const [postage, setPostage] = useState<IGetPostage>();
  const [codObjeto, setCodObjeto] = useState<string>('');
  const [brandOptions, setBrandOptions] = useState<IBrandOption[]>([]);
  const [incidentPostages, setIncidentPostages] = useState<
    { codPostage: string; data: IGetPostage; ticket: string }[]
  >([]);

  const { handleSetToken, handleSetTokenColeta, area, handleSetArea, brandId, handleSetBrandId } =
    useCorreios();
  const navigate = useNavigate();

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedBrandId = Number(event.target.value);
    const option = brandOptions.find((o) => o.id === selectedBrandId);
    if (!option) return;
    handleSetBrandId(selectedBrandId);
    handleSetArea(option.area);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setIncidentPostages([]);
    navigate('/new-shipment');
  };

  useEffect(() => {
    (async () => {
      // Busca brands e pré-seleciona ANTES de criar o token
      let fetchedBrands: IBrandOption[] = [];
      try {
        const brandsResponse = (await zafClient.request({
          url: '/api/v2/brands.json',
          type: 'GET',
        })) as { brands?: { id: number; name: string }[] };
        fetchedBrands = (brandsResponse.brands ?? []).reduce<IBrandOption[]>((acc, brand) => {
          const filter = matchBrandFilter(brand.name);
          if (filter) acc.push({ id: brand.id, label: filter.label, area: filter.area });
          return acc;
        }, []);
      } catch {
        fetchedBrands = [];
      }
      setBrandOptions(fetchedBrands);

      const responseArea = await getInternalComments(zafClient);
      const ticketBrand = await getCurrentTicketBrand();
      const matchedByTicket = ticketBrand?.name
        ? fetchedBrands.find((o) => {
            const filter = matchBrandFilter(ticketBrand.name ?? '');
            return filter && o.area === filter.area;
          })
        : undefined;

      const fallbackArea = responseArea?.area;
      const matchedByFallback = fallbackArea
        ? fetchedBrands.find((o) => o.area === fallbackArea)
        : undefined;

      const preSelected = matchedByTicket ?? matchedByFallback;
      let resolvedArea = area;
      if (preSelected) {
        handleSetBrandId(preSelected.id);
        handleSetArea(preSelected.area);
        resolvedArea = preSelected.area;
      }

      const userToken = await createToken(
        resolvedArea === 'SAC E-COM' ? '0079515169' : '0079515150'
      );
      const coletaToken = await createColetaToken();
      handleSetTokenColeta(coletaToken.data.token);

      if (userToken.status !== 201) return popupError('Erro ao criar token', false);

      handleSetToken(userToken.data);
      let currentToken = userToken.data.token;
      const onNewToken = (tokenData: unknown) => {
        handleSetToken(tokenData as typeof userToken.data);
        currentToken = (tokenData as typeof userToken.data).token;
      };

      const data = await zafClient.get('ticket.customField:custom_field_38372417820436');
      const codPostage = data['ticket.customField:custom_field_38372417820436'];
      const dataNuColeta = await zafClient.get('ticket.customField:custom_field_38374289099156');
      const nuColeta = String(dataNuColeta['ticket.customField:custom_field_38374289099156']);
      const dataPrazo = await zafClient.get('ticket.customField:custom_field_38375383498260');
      const prazo = String(dataPrazo['ticket.customField:custom_field_38375383498260']);
      const dataDataSolicitacao = await zafClient.get(
        'ticket.customField:custom_field_38374386331796'
      );
      const dataSolicitacao = String(
        dataDataSolicitacao['ticket.customField:custom_field_38374386331796']
      );

      const dataTipoPostagem = await zafClient.get(
        'ticket.customField:custom_field_38378192293268'
      );
      const tipoPostagem = dataTipoPostagem['ticket.customField:custom_field_38378192293268'];

      if (typeof codPostage === 'string') {
        setCodObjeto(codPostage);
        if (codPostage) {
          if (tipoPostagem === 'ca___coleta_domiciliar') {
            const novoObjeto = {
              codObjeto: codPostage,
              nuColeta: nuColeta,
              prazo: prazo.split('T')[0].split('-').reverse().join('/'),
              dataSolicitacao: dataSolicitacao.split('T')[0].split('-').reverse().join('/'),
              tipoPostal: {
                sigla: '',
                descricao: '',
                categoria: 'COLETA',
              },
              eventos: [],
            };
            setPostage({
              versao: '1.0',
              ehColeta: true,
              quantidade: 1,
              tipoResultado: 'coleta',
              objetos: [novoObjeto],
            });
          } else {
            const response = await withTokenRetry(
              (t) => getPostage(codPostage, t),
              currentToken,
              resolvedArea,
              onNewToken
            );

            if (response.status !== 200) return popupError('Erro ao buscar postagem', false);

            setPostage(response.data);
            if (postage != null) postage.ehColeta = false;
          }
        }
      }

      const dataTicket = await zafClient.get('ticket.id');
      const ticketId = dataTicket['ticket.id'];
      const ticketResponse = (await zafClient.request({
        url: `/api/v2/tickets/${ticketId}/incidents.json`,
        type: 'GET',
      })) as IZendeskIncidents;

      // Por algum motivo adiciona duplicado mesmo com set
      let indice = 0;
      const fields = ticketResponse.tickets[0] ? ticketResponse.tickets[0].fields : [];
      for (let i = 0; i < fields.length; ++i) if (fields[i].id === 38374263613844) indice = i;

      for (const ticket of ticketResponse.tickets) {
        const codIncidentPostage = ticket.fields[indice].value as string;

        if (codIncidentPostage !== '' && codIncidentPostage !== null) {
          if (
            incidentPostages.some(
              (incident) =>
                incident.codPostage === codIncidentPostage || incident.codPostage === codPostage
            )
          )
            continue;
          const response = await withTokenRetry(
            (t) => getPostage(codIncidentPostage, t),
            currentToken,
            resolvedArea,
            onNewToken
          );
          if (!response['data']['objetos'][0]['mensagem']) {
            await incidentPostages.push({
              codPostage: codIncidentPostage,
              data: response.data,
              ticket: String(ticket.id),
            });
          }
        }
      }

      const seen = new Set<string>();
      if (codPostage != null) seen.add(String(codPostage));
      setIncidentPostages(
        incidentPostages.filter((incident) => {
          const cod = String(incident.codPostage).trim().toUpperCase();
          if (seen.has(cod)) return false;
          seen.add(cod);
          return true;
        })
      );

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    brandOptions,
    brandId,
    handleChange,
    area,
    handleSubmit,
    loading,
    postage,
    codObjeto,
    incidentPostages,
  };
}
